export default function CocoaBeachDestinationsSection() {
  const stats = [
    {
      value: "65%",
      label: "of Cocoa Beach requests are cruise-related or surf travel with under 2 weeks notice",
    },
    {
      value: "3–7 days",
      label: "average notice for last-minute cruise bookings from Port Canaveral",
    },
    {
      value: "Mar–Nov",
      label: "peak cruise and hurricane swell season — passport renewals spike during these months",
    },
    {
      value: "48-hour",
      label: "shortest surf trip departure window we've accommodated for a Cocoa Beach surfer",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas",
      context: "The most common international destination for Cocoa Beach cruise passengers and private boaters. Port Canaveral offers year-round Bahamas cruises on multiple lines. Recreational boaters from Cocoa Beach and Cape Canaveral make regular runs to the Abacos, Bimini, and Grand Bahama. All passengers need valid passports for entry and re-entry.",
      urgency: "Last-minute cruise bookings from Port Canaveral often come together with 3–7 days of notice — too short for standard processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Costa Rica",
      context: "Top surf destination for Cocoa Beach surfers chasing Pacific swells. Costa Rica's Pacific coast — Tamarindo, Jaco, Santa Teresa, Pavones — draws Space Coast surfers during Atlantic hurricane season when storm-generated swells light up Central American breaks. Also popular with Cocoa Beach families for eco-adventure vacations.",
      urgency: "Surf trips are booked on short notice when swell forecasts solidify — often 1–2 weeks before departure",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Caribbean Cruises",
      context: "Cocoa Beach families and couples regularly cruise from Port Canaveral to the Eastern and Western Caribbean — the Bahamas, Jamaica, the Cayman Islands, Cozumel, St. Thomas, St. Maarten, and beyond. Disney Cruise Line from Port Canaveral is especially popular with Space Coast families. All passengers, including children, need valid passports.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Nicaragua",
      context: "Growing surf destination for Cocoa Beach surfers seeking uncrowded breaks. Nicaragua's Pacific coast — San Juan del Sur, Popoyo, Playa Maderas — offers consistent offshore winds and world-class waves. Also popular for yoga retreats and eco-tourism from Space Coast travelers.",
      urgency: "Nicaragua requires six months of passport validity — surfers booking on short notice often discover this requirement too late",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico",
      context: "Cancun, Riviera Maya, Los Cabos, and Puerto Vallarta are staple beach vacation destinations for Cocoa Beach families and couples. Orlando International Airport (MCO), about an hour from Cocoa Beach, offers extensive direct and connecting service to Mexico. Mexico requires six months of passport validity.",
      urgency: "Spring break bookings surge in January–February — passport discoveries spike in March for Cocoa Beach families",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "French Guiana",
      context: "Unique to Cocoa Beach's space industry community — the Guiana Space Centre in Kourou is a destination for launch photographers, space journalists, and aerospace contractors covering Ariane and Vega launches. French Guiana is an overseas department of France — a valid passport is required, and the six-month validity rule applies.",
      urgency: "Launch campaigns shift constantly — coverage assignments and payload integration trips are confirmed with 1–3 weeks of notice",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Cocoa Beach Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Cocoa Beach
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Cocoa Beach residents head to the Bahamas and Caribbean for last-minute cruises, Costa Rica and Nicaragua for surf trips, Mexico for beach vacations, and French Guiana for space industry launch coverage. These destinations create the most passport urgency for our Space Coast clients.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout the Space Coast</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Melbourne", href: "/melbourne" },
              { name: "Titusville", href: "/titusville" },
              { name: "Orlando", href: "/orlando" },
              { name: "Daytona Beach", href: "/daytona-beach" },
              { name: "Stuart", href: "/stuart" },
              { name: "Port St. Lucie", href: "/port-st-lucie" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}